import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/UI/Button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import logo from '../assets/Autograph-logo-01-01.svg';

const EmailVerificationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyEmail, user } = useAuth();

  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Use ref to prevent multiple verification attempts
  const verificationAttempted = useRef(false);

  useEffect(() => {
    const verify = async () => {
      // Prevent multiple verification attempts
      if (verificationAttempted.current) {
        return;
      }
      verificationAttempted.current = true;

      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        const result = await verifyEmail(token);

        if (result.success) {
          setStatus('success');
          // Auto-redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setStatus('error');
          setErrorMessage(result.error || 'Verification failed');
        }
      } catch (error) {
        setStatus('error');
        setErrorMessage('An unexpected error occurred');
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only depend on token, not on verifyEmail or navigate

  const handleContinue = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-ag-black-800 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src={logo}
            alt="Autograph Logo"
            className="h-16 w-auto"
          />
        </div>

        <div className="mt-8 bg-ag-black-600 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === 'verifying' && (
            <div className="text-center">
              <Loader2 className="h-16 w-16 text-green-500 animate-spin mx-auto" />
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Verifying your email...
              </h2>
              <p className="mt-2 text-gray-600">
                Please wait while we verify your email address.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                Email Verified!
              </h2>
              <p className="mt-2 text-gray-600">
                Your email has been successfully verified. You now have full access to all features.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Redirecting to dashboard...
              </p>
              <div className="mt-6">
                <Button
                  onClick={handleContinue}
                  className="w-full"
                  size="lg"
                >
                  {user ? 'Go to Dashboard' : 'Login to Continue'}
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h2 className="mt-6 text-2xl font-bold text-ag-white-200">
                Verification Failed
              </h2>
              <p className="mt-2 text-gray-400">
                {errorMessage || 'The verification link is invalid or has expired.'}
              </p>
              <div className="mt-6 space-y-3">
                {user ? (
                  <Link to="/dashboard">
                    <Button className="w-full" size="lg">
                      Go to Dashboard
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login">
                    <Button className="w-full" size="lg">
                      Login
                    </Button>
                  </Link>
                )}
                <p className="text-sm text-gray-400">
                  Need a new verification link?{' '}
                  <Link to="/login" className="text-primary-600 hover:text-primary-500">
                    Login and request a new one
                  </Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
